import React, { useState } from "react";
import { FaCamera, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import ChangePasswordModal from "../../components/ChangePasswordModal";

const ProfileSection = ({
  profileData,
  handleInputChange,
  handleProfileImage,
  handleSave,
  isModified,
  isSaving,
  uploadProgress,
  error,
}) => {
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-6 text-gray-800">
        Profile Information
      </h2>

      {/* Profile Picture + Info */}
      <div className="flex items-start space-x-6 mb-8 pb-8 border-b border-gray-200 flex-wrap">
        <div className="relative group mb-4 sm:mb-0">
          <img
            src={
              profileData.profilePictureUrl ||
              "https://via.placeholder.com/120x120.png?text=Profile"
            }
            alt="Profile"
            className="w-28 h-28 rounded-xl object-cover border-2 border-gray-200"
          />
          <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded-xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-200">
            <div className="text-center">
              <FaCamera className="text-white text-xl mx-auto mb-1" />
              <span className="text-white text-xs">Change</span>
            </div>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfileImage}
            />
          </label>
        </div>

        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {profileData.firstName} {profileData.lastName}
          </h3>
          <div className="space-y-2">
            <p className="text-gray-600 flex items-center">
              <FaEnvelope className="mr-3 text-gray-400 w-4" />
              {profileData.email}
            </p>
            <p className="text-gray-600 flex items-center">
              <FaPhone className="mr-3 text-gray-400 w-4" />
              {profileData.mobile || "Not provided"}
            </p>
            <p className="text-gray-600 flex items-center">
              <FaMapMarkerAlt className="mr-3 text-gray-400 w-4" />
              {profileData.city && profileData.state
                ? `${profileData.city}, ${profileData.state}`
                : "Location not provided"}
            </p>
          </div>
        </div>
      </div>

      {/* Editable Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          ["First Name", "firstName"],
          ["Last Name", "lastName"],
          ["City", "city"],
          ["State", "state"],
          ["ZIP Code", "zip"],
          ["Headquarters Address", "headquarters"],
        ].map(([label, field]) => (
          <div key={field}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {label}
            </label>
            <input
              type="text"
              value={profileData[field]}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50"
            />
          </div>
        ))}

        {/* 🔹 Mobile Number Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mobile Number
          </label>
          <div className="flex items-center border border-gray-300 rounded-lg bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 transition-all overflow-hidden">
            <input
              type="text"
              value={profileData.countryCode !== undefined ? profileData.countryCode : "+1"}
              onChange={(e) => {
                const newCode = e.target.value.replace(/[^+\d]/g, '').slice(0, 5);
                handleInputChange("countryCode", newCode);
              }}
              placeholder="+1"
              disabled
              className="w-16 px-2 py-2.5 text-gray-800 font-medium bg-gray-100 border-0 border-r border-gray-300 focus:outline-none text-center cursor-not-allowed select-none"
            />
            <input
              type="tel"
              value={profileData.mobile || ""}
              onChange={(e) => {
                const newPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
                handleInputChange("mobile", newPhone);
              }}
              placeholder="10-digit number"
              className="flex-1 px-4 py-2.5 text-gray-800 bg-transparent border-0 focus:outline-none tracking-wide"
            />
          </div>
        </div>
      </div>

      {/* 🔹 Account Security Section */}
      <div className="mt-10 border-t border-gray-200 pt-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Security</h3>
        <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div>
            <h4 className="font-medium text-gray-900">Password</h4>
            <p className="text-sm text-gray-500 mt-1">Ensure your account is using a long, random password to stay secure.</p>
          </div>
          <button
            type="button"
            onClick={() => setIsPasswordModalOpen(true)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-600 hover:border-blue-300 transition-colors shadow-sm"
          >
            Change Password
          </button>
        </div>
      </div>

      {/* Footer Section */}
      <div className="flex justify-end mt-6 flex-wrap gap-3">
        {error && (
          <p className="text-red-600 mr-auto self-center text-sm">{error}</p>
        )}
        {isSaving && uploadProgress !== null && (
          <p className="text-gray-600 mr-4 self-center text-sm">
            Saving Changes...
          </p>
        )}
        <button
          onClick={handleSave}
          disabled={!isModified || isSaving}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            isModified && !isSaving
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isSaving ? "Saving..." : "Save Profile Changes"}
        </button>
      </div>

      <ChangePasswordModal 
        isOpen={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)} 
      />
    </div>
  );
};

export default ProfileSection;
